import React, { useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { Label } from "../../ui/label";
import { Input } from "../../ui/input";
import { Button } from "../../ui/button";

function emptyCondicoes() {
  return { pontos: [], clausulas: [] };
}

function parse(value) {
  if (!value || typeof value !== "object") return emptyCondicoes();
  return {
    pontos: Array.isArray(value.pontos) ? value.pontos : [],
    clausulas: Array.isArray(value.clausulas) ? value.clausulas : [],
  };
}

export function CondicoesEditor({ value, onChange }) {
  const data = parse(value);
  const [novoPonto, setNovoPonto] = useState("");
  const [novaClausula, setNovaClausula] = useState({ titulo: "", texto: "" });

  const emit = (next) => onChange(next);

  const addPonto = () => {
    const txt = novoPonto.trim();
    if (!txt) return;
    emit({ ...data, pontos: [...data.pontos, txt] });
    setNovoPonto("");
  };

  const removePonto = (idx) =>
    emit({ ...data, pontos: data.pontos.filter((_, i) => i !== idx) });

  const updatePonto = (idx, val) => {
    const pontos = [...data.pontos];
    pontos[idx] = val;
    emit({ ...data, pontos });
  };

  const addClausula = () => {
    if (!novaClausula.titulo.trim() || !novaClausula.texto.trim()) return;
    emit({ ...data, clausulas: [...data.clausulas, { ...novaClausula }] });
    setNovaClausula({ titulo: "", texto: "" });
  };

  const removeClausula = (idx) =>
    emit({ ...data, clausulas: data.clausulas.filter((_, i) => i !== idx) });

  const updateClausula = (idx, field, val) => {
    const clausulas = data.clausulas.map((c, i) =>
      i === idx ? { ...c, [field]: val } : c
    );
    emit({ ...data, clausulas });
  };

  return (
    <div className="space-y-5">
      <div className="rounded-lg border border-statusInfo/20 bg-statusInfoBg/40 p-4 space-y-3">
        <p className="text-xs font-semibold text-statusInfo uppercase tracking-wide">
          Pontos Chave (caixa azul)
        </p>
        {data.pontos.map((ponto, idx) => (
          <div key={idx} className="flex gap-2 items-start">
            <textarea
              value={ponto}
              onChange={e => updatePonto(idx, e.target.value)}
              rows={2}
              className="flex-1 border border-border rounded-md px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300 resize-none"
            />
            <button
              type="button"
              onClick={() => removePonto(idx)}
              className="mt-1 p-1.5 rounded text-muted-foreground hover:text-red-500 hover:bg-statusErrorBg transition-colors shrink-0"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        ))}
        <div className="flex gap-2">
          <Input
            value={novoPonto}
            onChange={e => setNovoPonto(e.target.value)}
            onKeyDown={e => e.key === "Enter" && (e.preventDefault(), addPonto())}
            placeholder="Novo ponto chave..."
            className="text-sm"
          />
          <Button type="button" size="sm" variant="outline" onClick={addPonto} className="shrink-0 gap-1">
            <Plus className="w-3.5 h-3.5" /> Adicionar
          </Button>
        </div>
      </div>

      <div className="rounded-lg border border-border bg-muted/40 p-4 space-y-4">
        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
          Cláusulas Contratuais (caixa cinza)
        </p>
        {data.clausulas.map((c, idx) => (
          <div key={idx} className="space-y-1.5 border border-border rounded-lg p-3 bg-card relative">
            <button
              type="button"
              onClick={() => removeClausula(idx)}
              className="absolute top-2 right-2 p-1 rounded text-slate-300 hover:text-red-500 hover:bg-statusErrorBg transition-colors"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
            <div>
              <Label className="text-xs text-muted-foreground">Título</Label>
              <Input
                value={c.titulo}
                onChange={e => updateClausula(idx, "titulo", e.target.value)}
                placeholder="Ex: Preço adicional"
                className="text-sm mt-1"
              />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Texto</Label>
              <textarea
                value={c.texto}
                onChange={e => updateClausula(idx, "texto", e.target.value)}
                rows={3}
                className="w-full mt-1 border border-border rounded-md px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300 resize-y"
              />
            </div>
          </div>
        ))}
        <div className="space-y-2 border border-dashed border-border rounded-lg p-3">
          <p className="text-xs text-muted-foreground">Nova cláusula</p>
          <Input
            value={novaClausula.titulo}
            onChange={e => setNovaClausula(v => ({ ...v, titulo: e.target.value }))}
            placeholder="Título (ex: Risco integral do processo)"
            className="text-sm"
          />
          <textarea
            value={novaClausula.texto}
            onChange={e => setNovaClausula(v => ({ ...v, texto: e.target.value }))}
            placeholder="Texto da cláusula..."
            rows={3}
            className="w-full border border-border rounded-md px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300 resize-y"
          />
          <Button type="button" size="sm" variant="outline" onClick={addClausula} className="gap-1">
            <Plus className="w-3.5 h-3.5" /> Adicionar cláusula
          </Button>
        </div>
      </div>
    </div>
  );
}
